import { z } from 'zod/v4';
import { ENTITY_HISTORY_ENTITY_TYPES } from '../../constants.js';

/**
 * Where a profile change came from when it was not typed into the profile —
 * the questionnaire whose sync wrote the answers. The UI links to it.
 */
export const CompanyProfileHistorySourceSchema = z.object({
  from: z.string().optional(),
  id: z.string().optional(),
  requestId: z.string().optional(),
  forCompanyId: z.string().optional(),
});

/** `metadata` of a `COMPANY_PROFILE` entry. */
export const CompanyProfileHistoryMetadataSchema = z.object({
  entityType: z.literal(ENTITY_HISTORY_ENTITY_TYPES.COMPANY_PROFILE),
  /** The profile template the save was against (`ABOUT`, `OTHER_IDS`, …). */
  templateId: z.string().nullable(),
  source: CompanyProfileHistorySourceSchema.optional(),
  /** On a `CREATED` (link/create) entry: the connection type in effect. */
  connectionType: z.string().optional(),
  /**
   * On a `CREATED` entry for an asset template (`AUDITS`, `CERTIFICATES`,
   * `DOCUMENTS`): the shared asset. `assetLabel` is best-effort — the blobs are
   * untyped, so the UI falls back to the template name when it is absent.
   */
  assetId: z.string().optional(),
  assetLabel: z.string().nullable().optional(),
});

export type CompanyProfileHistoryMetadata = z.infer<
  typeof CompanyProfileHistoryMetadataSchema
>;

/** `metadata` of an `AI_PROCESSING_JOB` entry. */
export const AiProcessingJobHistoryMetadataSchema = z.object({
  entityType: z.literal(ENTITY_HISTORY_ENTITY_TYPES.AI_PROCESSING_JOB),
  /** The workflow / summary id the job tracks (`ai_processing_status.asset_id`). */
  assetId: z.string(),
  /** On a resolution: why the job left the queue (`CONSUMED`, `EXPIRED`, …). */
  reason: z.string().optional(),
  /** On a `CONSUMED` resolution: the record that used the result. */
  entityRef: z
    .object({ entityType: z.string(), entityId: z.string() })
    .optional(),
});

export type AiProcessingJobHistoryMetadata = z.infer<
  typeof AiProcessingJobHistoryMetadataSchema
>;

/** `metadata` of a `CMM_ORDER_LINK` entry. */
export const CmmOrderLinkHistoryMetadataSchema = z.object({
  entityType: z.literal(ENTITY_HISTORY_ENTITY_TYPES.CMM_ORDER_LINK),
  /** The CMM record that extended the order. Gone once this is written. */
  cmmOrderId: z.string(),
  /**
   * What ended the link: a delete in CMM, or the Order Hub order's own delete
   * reaching CMM.
   */
  trigger: z.enum(['CMM_DELETE', 'ORDER_HUB_DELETE']),
  /** On a `CMM_DELETE`: Hub lines whose CMM markers were cleared. */
  clearedOrderLineIds: z.array(z.string()).optional(),
  /** On a `CMM_DELETE`: documents CMM had put on the order, now removed. */
  removedDocumentIds: z.array(z.string()).optional(),
});

export type CmmOrderLinkHistoryMetadata = z.infer<
  typeof CmmOrderLinkHistoryMetadataSchema
>;

/** `metadata` of a `USER_ACCESS` entry. A `MEMBERSHIP` entry (the user
 *  added to or removed from a company — the company is its
 *  `for_company_id`) carries only `entityType`. */
export const UserAccessHistoryMetadataSchema = z.object({
  entityType: z.literal(ENTITY_HISTORY_ENTITY_TYPES.USER_ACCESS),
  /** On a `SESSIONS` entry: how many live sessions the revocation ended. */
  revokedSessionCount: z.number().int().nonnegative().optional(),
  /** On a `SESSIONS` entry: `BLOCKED` when the account was blocked,
   *  `ADMIN_REVOKE` for an explicit "revoke sessions" (security incident). */
  reason: z.enum(['BLOCKED', 'ADMIN_REVOKE']).optional(),
  /** On a `ROLE` entry: the role that was assigned or removed. */
  roleKey: z.string().optional(),
  /** On a `RECORD_ACCESS` entry: the record that was shared, denied or cleared. */
  resource: z
    .object({ permission: z.string(), type: z.string(), id: z.string() })
    .optional(),
  /** On a `DELETED` entry (the user was deleted): what was removed with them. */
  removedExceptionCount: z.number().int().nonnegative().optional(),
});

export type UserAccessHistoryMetadata = z.infer<
  typeof UserAccessHistoryMetadataSchema
>;

/** `metadata` of an `IAM_ROLE` entry. */
export const IamRoleHistoryMetadataSchema = z.object({
  entityType: z.literal(ENTITY_HISTORY_ENTITY_TYPES.IAM_ROLE),
  /** The role's key in its tenant — what people read; `entity_id` is the row id. */
  roleKey: z.string(),
  roleName: z.string().optional(),
});

export type IamRoleHistoryMetadata = z.infer<
  typeof IamRoleHistoryMetadataSchema
>;

/** `metadata` of a `SERVICE_ACCOUNT` entry. Never carries the key or its hash. */
export const ServiceAccountHistoryMetadataSchema = z.object({
  entityType: z.literal(ENTITY_HISTORY_ENTITY_TYPES.SERVICE_ACCOUNT),
  /** The API key's label (also the account's name). */
  label: z.string().optional(),
  apiKeyId: z.string().optional(),
  /** On a `CREATED` entry, or a `ROLE` entry: the role involved. */
  roleKey: z.string().optional(),
});

export type ServiceAccountHistoryMetadata = z.infer<
  typeof ServiceAccountHistoryMetadataSchema
>;

/** `metadata` of an `ORDER_HUB_TRACING_LINK` entry: what a later undo would need. */
export const OrderHubTracingLinkHistoryMetadataSchema = z.object({
  entityType: z.literal(ENTITY_HISTORY_ENTITY_TYPES.ORDER_HUB_TRACING_LINK),
  tracingOrderId: z.string(),
  direction: z.enum(['ORDER_HUB', 'TRACING']).optional(),
  lineMode: z.enum(['MERGE', 'REPLACE']).optional(),
  pairedLines: z
    .array(z.object({ orderLineId: z.string(), tracingLineId: z.string() }))
    .optional(),
  /** Tracing lines that became new hub lines. */
  addedToOrderHub: z.array(z.string()).optional(),
  /** Hub lines that became new Tracing lines. */
  addedToTracing: z.array(z.string()).optional(),
  /** Replace mode: the request's lines that were removed. */
  removedFromTracing: z.array(z.string()).optional(),
  /** Received link: the hub lines holding the reference. */
  orderLineIds: z.array(z.string()).optional(),
});

export type OrderHubTracingLinkHistoryMetadata = z.infer<
  typeof OrderHubTracingLinkHistoryMetadataSchema
>;

export const OrderHubOrderHistoryMetadataSchema = z.object({
  entityType: z.literal(ENTITY_HISTORY_ENTITY_TYPES.ORDER_HUB_ORDER),
  origin: z.enum(['TRACING']).optional(),
  orderNumber: z.string().optional(),
  lineCount: z.number().int().nonnegative().optional(),
  lines: z
    .array(z.object({ orderLineId: z.string(), tracingOrderId: z.string() }))
    .optional(),
  orderLineIds: z.array(z.string()).optional(),
  tracingOrderIds: z.array(z.string()).optional(),
  documentIds: z.array(z.string()).optional(),
});

export type OrderHubOrderHistoryMetadata = z.infer<
  typeof OrderHubOrderHistoryMetadataSchema
>;

export const AutomationRuleQuotaHistoryMetadataSchema = z.object({
  entityType: z.literal(ENTITY_HISTORY_ENTITY_TYPES.AUTOMATION_RULE_QUOTA),
  usedRules: z.number().int().nonnegative(),
});

export type AutomationRuleQuotaHistoryMetadata = z.infer<
  typeof AutomationRuleQuotaHistoryMetadataSchema
>;

/** `metadata` of a `COMPANY_DOCUMENT` entry. */
export const CompanyDocumentHistoryMetadataSchema = z.object({
  entityType: z.literal(ENTITY_HISTORY_ENTITY_TYPES.COMPANY_DOCUMENT),
  /** The document's name when the entry was written — it can be renamed later. */
  documentName: z.string(),
});

export type CompanyDocumentHistoryMetadata = z.infer<
  typeof CompanyDocumentHistoryMetadataSchema
>;

/** `metadata` of a `DEVELOPMENT_TAG_FEATURE` entry. */
export const DevelopmentTagFeatureHistoryMetadataSchema = z.object({
  entityType: z.literal(ENTITY_HISTORY_ENTITY_TYPES.DEVELOPMENT_TAG_FEATURE),
});

export type DevelopmentTagFeatureHistoryMetadata = z.infer<
  typeof DevelopmentTagFeatureHistoryMetadataSchema
>;

/** `metadata` of an `ALLOWED_ORIGIN` entry. */
export const AllowedOriginHistoryMetadataSchema = z.object({
  entityType: z.literal(ENTITY_HISTORY_ENTITY_TYPES.ALLOWED_ORIGIN),
  /** On a `DELETED` entry: what the row allowed, since the row is gone. */
  origin: z.string().optional(),
  name: z.string().nullable().optional(),
});

export type AllowedOriginHistoryMetadata = z.infer<
  typeof AllowedOriginHistoryMetadataSchema
>;

/** `metadata` of a `TRANSLATION` entry: the key, split back out of `entity_id`. */
export const TranslationHistoryMetadataSchema = z.object({
  entityType: z.literal(ENTITY_HISTORY_ENTITY_TYPES.TRANSLATION),
  namespace: z.string(),
  key: z.string(),
});

export type TranslationHistoryMetadata = z.infer<
  typeof TranslationHistoryMetadataSchema
>;
/** `metadata` of a `USER_PROFILE` entry. */
export const UserProfileHistoryMetadataSchema = z.object({
  entityType: z.literal(ENTITY_HISTORY_ENTITY_TYPES.USER_PROFILE),
});

export type UserProfileHistoryMetadata = z.infer<
  typeof UserProfileHistoryMetadataSchema
>;

/** `metadata` of a `USER_PREFERENCE` entry. */
export const UserPreferenceHistoryMetadataSchema = z.object({
  entityType: z.literal(ENTITY_HISTORY_ENTITY_TYPES.USER_PREFERENCE),
});

export type UserPreferenceHistoryMetadata = z.infer<
  typeof UserPreferenceHistoryMetadataSchema
>;

/** `metadata` of a `QUESTIONNAIRE` entry. */
export const QuestionnaireHistoryMetadataSchema = z.object({
  entityType: z.literal(ENTITY_HISTORY_ENTITY_TYPES.QUESTIONNAIRE),
  /** On a `CREATED` entry: the questionnaire or template it was copied from. */
  copiedFromId: z.string().optional(),
  /** On a `CREATED` entry: how much content the copy brought along. */
  sectionCount: z.number().int().nonnegative().optional(),
  questionCount: z.number().int().nonnegative().optional(),
  /** On a `DELETED` entry: its name, since the row is gone. */
  name: z.string().nullable().optional(),
});

export type QuestionnaireHistoryMetadata = z.infer<
  typeof QuestionnaireHistoryMetadataSchema
>;

/** `metadata` of a `COMPANY_MERGE` entry. */
export const CompanyMergeHistoryMetadataSchema = z.object({
  entityType: z.literal(ENTITY_HISTORY_ENTITY_TYPES.COMPANY_MERGE),
  /** The duplicate company merged away — the target is the entry's company. */
  sourceCompanyId: z.string(),
  /** On a group failure: the registry group that failed. */
  groupKey: z.string().optional(),
  error: z.string().optional(),
  /** On a completion: rows moved per group. */
  rowsMovedByGroup: z.record(z.string(), z.number()).optional(),
});

export type CompanyMergeHistoryMetadata = z.infer<
  typeof CompanyMergeHistoryMetadataSchema
>;

/**
 * What moved a `QUESTIONNAIRE_REQUEST` without anyone acting on the request
 * itself: a builder save of its questionnaire that added a question (and so
 * reopened it) or moved the questionnaire's due date.
 */
export const QUESTIONNAIRE_REQUEST_HISTORY_TRIGGERS = [
  'QUESTION_ADDED',
  'QUESTIONNAIRE_DUE_DATE',
] as const;

/** `metadata` of a `QUESTIONNAIRE_REQUEST` entry. */
export const QuestionnaireRequestHistoryMetadataSchema = z.object({
  entityType: z.literal(ENTITY_HISTORY_ENTITY_TYPES.QUESTIONNAIRE_REQUEST),
  /** The questionnaire the request answers. */
  questionnaireId: z.string().optional(),
  /** Set when a questionnaire save, not a request action, moved it. */
  trigger: z.enum(QUESTIONNAIRE_REQUEST_HISTORY_TRIGGERS).optional(),
  /** On a `DELETED` entry: the status it had, since the row now says `DELETED`. */
  status: z.string().nullable().optional(),
});

export type QuestionnaireRequestHistoryMetadata = z.infer<
  typeof QuestionnaireRequestHistoryMetadataSchema
>;

/** What moved a `COLLABORATION` without its reviewer or collaborator acting on it. */
export const COLLABORATION_HISTORY_TRIGGERS = [
  'FIRST_OPEN',
  'SUPPLIER_EDIT',
  'REMINDER',
] as const;

/** `metadata` of a `COLLABORATION` entry. */
export const CollaborationHistoryMetadataSchema = z.object({
  entityType: z.literal(ENTITY_HISTORY_ENTITY_TYPES.COLLABORATION),
  /** The shared entity — a tracing order id. */
  parentEntityId: z.string(),
  /** The `tracing_nodes` row the collaboration sits at. */
  tracingNodeId: z.string(),
  trigger: z.enum(COLLABORATION_HISTORY_TRIGGERS).optional(),
  /** The invite or review message that came with the change. */
  message: z.string().optional(),
  /** On a `DELETED` entry: the status it had. */
  status: z.string().nullable().optional(),
});

export type CollaborationHistoryMetadata = z.infer<
  typeof CollaborationHistoryMetadataSchema
>;

/** `metadata` of a `QUESTIONNAIRE_ANSWER` entry. */
export const QuestionnaireAnswerHistoryMetadataSchema = z.object({
  entityType: z.literal(ENTITY_HISTORY_ENTITY_TYPES.QUESTIONNAIRE_ANSWER),
  /** The questionnaire the request answers. */
  questionnaireId: z.string().optional(),
  /** The answer was filled in from the company profile, not typed. */
  autoAnswered: z.boolean().optional(),
});

export type QuestionnaireAnswerHistoryMetadata = z.infer<
  typeof QuestionnaireAnswerHistoryMetadataSchema
>;

/** `metadata` of a `QUESTIONNAIRE_COMMENT` entry. */
export const QuestionnaireCommentHistoryMetadataSchema = z.object({
  entityType: z.literal(ENTITY_HISTORY_ENTITY_TYPES.QUESTIONNAIRE_COMMENT),
  /** The question the comment is on. */
  questionId: z.string().optional(),
});

export type QuestionnaireCommentHistoryMetadata = z.infer<
  typeof QuestionnaireCommentHistoryMetadataSchema
>;

/** `metadata` of a `USER_EMAIL_SUPPRESSION` entry. */
export const UserEmailSuppressionHistoryMetadataSchema = z.object({
  entityType: z.literal(ENTITY_HISTORY_ENTITY_TYPES.USER_EMAIL_SUPPRESSION),
  /** Why SES had suppressed the address; also the entry's sub type. */
  reason: z.enum(['BOUNCE', 'COMPLAINT']),
  /** When SES last put the address on the list (ISO timestamp). */
  suppressedAt: z.string().nullable(),
  /** What lifted it — today only a login code request. */
  trigger: z.literal('LOGIN_CODE_REQUEST'),
});

export type UserEmailSuppressionHistoryMetadata = z.infer<
  typeof UserEmailSuppressionHistoryMetadataSchema
>;

/** `metadata` of an `ASSESSMENT_BLUEPRINT` entry. */
export const AssessmentBlueprintHistoryMetadataSchema = z.object({
  entityType: z.literal(ENTITY_HISTORY_ENTITY_TYPES.ASSESSMENT_BLUEPRINT),
});

export type AssessmentBlueprintHistoryMetadata = z.infer<
  typeof AssessmentBlueprintHistoryMetadataSchema
>;

/** `metadata` of a `RULE_TEMPLATE` entry. */
export const RuleTemplateHistoryMetadataSchema = z.object({
  entityType: z.literal(ENTITY_HISTORY_ENTITY_TYPES.RULE_TEMPLATE),
  /** On a `CREATED` entry: the template it was copied from. */
  copiedFromId: z.string().optional(),
  /** On a `CREATED` entry for a copy: how many rules the copy brought along. */
  ruleCount: z.number().int().nonnegative().optional(),
  /** On a `DELETED` entry: its title, since the row is gone. */
  title: z.string().optional(),
});

export type RuleTemplateHistoryMetadata = z.infer<
  typeof RuleTemplateHistoryMetadataSchema
>;

/** `metadata` of a `RISK` entry. */
export const RiskHistoryMetadataSchema = z.object({
  entityType: z.literal(ENTITY_HISTORY_ENTITY_TYPES.RISK),
});

export type RiskHistoryMetadata = z.infer<typeof RiskHistoryMetadataSchema>;

/** `metadata` of a `CERTIFICATION_RULE` entry. */
export const CertificationRuleHistoryMetadataSchema = z.object({
  entityType: z.literal(ENTITY_HISTORY_ENTITY_TYPES.CERTIFICATION_RULE),
});

export type CertificationRuleHistoryMetadata = z.infer<
  typeof CertificationRuleHistoryMetadataSchema
>;

/** `metadata` of a `CAPA_ACTION` entry. */
export const CapaActionHistoryMetadataSchema = z.object({
  entityType: z.literal(ENTITY_HISTORY_ENTITY_TYPES.CAPA_ACTION),
  /**
   * On an `ARCHIVED` entry: the action was archived as a linked child of a
   * plan being archived, not picked itself.
   */
  withPlan: z.boolean().optional(),
  /**
   * On an activity entry (the CAPA activity log, `action_histories`): the id
   * of the activity-log row it mirrors, which the CAPA history routes publish.
   */
  historyId: z.string().optional(),
  /** On an activity entry: the log's own event word. */
  activity: z
    .enum(['created', 'added', 'changed', 'removed', 'deleted'])
    .optional(),
  /**
   * On an activity entry with no `property` (a creation or a deletion): the
   * log's states, which have no field to sit under in `changes` — `{ id }`
   * on a creation, the whole action row on a deletion.
   */
  prevState: z.unknown().optional(),
  newState: z.unknown().optional(),
});

export type CapaActionHistoryMetadata = z.infer<
  typeof CapaActionHistoryMetadataSchema
>;

/** `metadata` of a `DOCUMENT_TYPE` entry. */
export const DocumentTypeHistoryMetadataSchema = z.object({
  entityType: z.literal(ENTITY_HISTORY_ENTITY_TYPES.DOCUMENT_TYPE),
});

export type DocumentTypeHistoryMetadata = z.infer<
  typeof DocumentTypeHistoryMetadataSchema
>;

/** `metadata` of an `AUDIT_TYPE` entry. */
export const AuditTypeHistoryMetadataSchema = z.object({
  entityType: z.literal(ENTITY_HISTORY_ENTITY_TYPES.AUDIT_TYPE),
  /** On a `DELETED` entry: the audit type's name, since the row is gone. */
  name: z.string().nullable().optional(),
});

export type AuditTypeHistoryMetadata = z.infer<
  typeof AuditTypeHistoryMetadataSchema
>;

/** `metadata` of an `AUDIT` entry. */
export const AuditHistoryMetadataSchema = z.object({
  entityType: z.literal(ENTITY_HISTORY_ENTITY_TYPES.AUDIT),
  /** On a `DELETED` entry: the audit's name, since the row is gone. */
  name: z.string().nullable().optional(),
  /** On a `DELETED` entry: why it went — today only an amfori supplier unlink. */
  reason: z.enum(['AMFORI_SUPPLIER_UNLINKED']).optional(),
});

export type AuditHistoryMetadata = z.infer<typeof AuditHistoryMetadataSchema>;

/** `metadata` of an `AUDIT_FINDING` entry. */
export const AuditFindingHistoryMetadataSchema = z.object({
  entityType: z.literal(ENTITY_HISTORY_ENTITY_TYPES.AUDIT_FINDING),
  /** On a `DELETED` entry: the finding's title, since the row is gone. */
  title: z.string().nullable().optional(),
  /** On a `DELETED` entry: why it went — today only an amfori supplier unlink. */
  reason: z.enum(['AMFORI_SUPPLIER_UNLINKED']).optional(),
});

export type AuditFindingHistoryMetadata = z.infer<
  typeof AuditFindingHistoryMetadataSchema
>;

/** `metadata` of a `WEBSHOP` entry. */
export const WebshopHistoryMetadataSchema = z.object({
  entityType: z.literal(ENTITY_HISTORY_ENTITY_TYPES.WEBSHOP),
});

export type WebshopHistoryMetadata = z.infer<
  typeof WebshopHistoryMetadataSchema
>;

/** `metadata` of a `STYLE` entry. */
export const StyleHistoryMetadataSchema = z.object({
  entityType: z.literal(ENTITY_HISTORY_ENTITY_TYPES.STYLE),
  /** The bulk import that wrote the change (source `IMPORT`). */
  bulkImportId: z.string().optional(),
  /** One bulk edit, archive or unarchive request across many rows. */
  batchId: z.string().optional(),
  /** On an `ARCHIVED` entry: the descendants archived along with the row. */
  affectedDescendantIds: z.array(z.string()).optional(),
});

export type StyleHistoryMetadata = z.infer<typeof StyleHistoryMetadataSchema>;

/** `metadata` of a `PLATFORM_FEATURE` entry. */
export const PlatformFeatureHistoryMetadataSchema = z.object({
  entityType: z.literal(ENTITY_HISTORY_ENTITY_TYPES.PLATFORM_FEATURE),
});

export type PlatformFeatureHistoryMetadata = z.infer<
  typeof PlatformFeatureHistoryMetadataSchema
>;

/** `metadata` of a `COMPANY_FEATURE` entry. */
export const CompanyFeatureHistoryMetadataSchema = z.object({
  entityType: z.literal(ENTITY_HISTORY_ENTITY_TYPES.COMPANY_FEATURE),
});

export type CompanyFeatureHistoryMetadata = z.infer<
  typeof CompanyFeatureHistoryMetadataSchema
>;

/** `metadata` of a `TAG_IDENTIFICATION` entry. */
export const TagIdentificationHistoryMetadataSchema = z.object({
  entityType: z.literal(ENTITY_HISTORY_ENTITY_TYPES.TAG_IDENTIFICATION),
});

export type TagIdentificationHistoryMetadata = z.infer<
  typeof TagIdentificationHistoryMetadataSchema
>;

/** `metadata` of a `FACILITY_PROCESS` entry. */
export const FacilityProcessHistoryMetadataSchema = z.object({
  entityType: z.literal(ENTITY_HISTORY_ENTITY_TYPES.FACILITY_PROCESS),
});

export type FacilityProcessHistoryMetadata = z.infer<
  typeof FacilityProcessHistoryMetadataSchema
>;

/** `metadata` of an `AUDIT_ACCESS_REQUEST` entry. */
export const AuditAccessRequestHistoryMetadataSchema = z.object({
  entityType: z.literal(ENTITY_HISTORY_ENTITY_TYPES.AUDIT_ACCESS_REQUEST),
  /** The audit access was asked for. */
  auditId: z.string(),
});

export type AuditAccessRequestHistoryMetadata = z.infer<
  typeof AuditAccessRequestHistoryMetadataSchema
>;

/** `metadata` of a `PRODUCT_DATA_POINT` entry. */
export const ProductDataPointHistoryMetadataSchema = z.object({
  entityType: z.literal(ENTITY_HISTORY_ENTITY_TYPES.PRODUCT_DATA_POINT),
});

export type ProductDataPointHistoryMetadata = z.infer<
  typeof ProductDataPointHistoryMetadataSchema
>;

/** `metadata` of a `PRODUCT_DATA_POINT_GROUP` entry. */
export const ProductDataPointGroupHistoryMetadataSchema = z.object({
  entityType: z.literal(ENTITY_HISTORY_ENTITY_TYPES.PRODUCT_DATA_POINT_GROUP),
});

export type ProductDataPointGroupHistoryMetadata = z.infer<
  typeof ProductDataPointGroupHistoryMetadataSchema
>;

/** `metadata` of an `AMFORI_SUPPLIER_LINK` entry. */
export const AmforiSupplierLinkHistoryMetadataSchema = z.object({
  entityType: z.literal(ENTITY_HISTORY_ENTITY_TYPES.AMFORI_SUPPLIER_LINK),
  /** On a sync `CREATED`: the sync run that linked the site. */
  syncHistoryId: z.string().optional(),
  /** On a sync `CREATED`: the site's name in amfori. */
  amforiSiteName: z.string().nullable().optional(),
  /** On a sync `CREATED`: how the matcher found the supplier. */
  matchedVia: z.string().optional(),
  /** On an `UNLINKED`: whether the supplier's synced amfori audits and
   *  findings were deleted with it (the sync report's unlink). */
  syncedDataRemoved: z.boolean().optional(),
  /** On an `UNLINKED` that removed synced data: how many went. */
  auditsDeleted: z.number().optional(),
  findingsDeleted: z.number().optional(),
  /** On an entry the backfill wrote for a link from before this history: how
   *  its company was inferred — the step log of the sync run that wrote it,
   *  the only amfori-connected brand with the supplier in its network, or the
   *  editing user's company. */
  ownerInferredFrom: z
    .enum(['SYNC_STEP_LOG', 'SOLE_BRAND', 'EDITOR_COMPANY'])
    .optional(),
});

export type AmforiSupplierLinkHistoryMetadata = z.infer<
  typeof AmforiSupplierLinkHistoryMetadataSchema
>;

/** `metadata` of an `AMFORI_SYNC_WARNING` entry. */
export const AmforiSyncWarningHistoryMetadataSchema = z.object({
  entityType: z.literal(ENTITY_HISTORY_ENTITY_TYPES.AMFORI_SYNC_WARNING),
  /** The sync run the warning was dismissed or restored from, or the one
   *  that brought it back. */
  syncHistoryId: z.string().optional(),
  /** On a `RESTORED` by the sync: what changed since the brand dismissed it —
   *  the site's amfori audit count, and/or the matched companies by id. */
  resurfaceReason: z
    .object({
      auditCount: z.object({ from: z.number(), to: z.number() }).optional(),
      companies: z
        .object({
          added: z.array(z.string()),
          removed: z.array(z.string()),
          reclassified: z.array(z.string()),
        })
        .optional(),
    })
    .optional(),
});

export type AmforiSyncWarningHistoryMetadata = z.infer<
  typeof AmforiSyncWarningHistoryMetadataSchema
>;

/**
 * The registry of per-entity-type metadata shapes. `metadata` is generic
 * jsonb in the table; this is where it is typed, one member per entity type.
 */
/** `metadata` of a `SOCIAL_SCORE_TEMPLATE` entry. */
export const SocialScoreTemplateHistoryMetadataSchema = z.object({
  entityType: z.literal(ENTITY_HISTORY_ENTITY_TYPES.SOCIAL_SCORE_TEMPLATE),
  /** The template's name as it stood, so a deleted template still reads. */
  templateName: z.string().optional(),
  /**
   * On a finding-level entry (`entity_sub_type` set): the row's human
   * reference. It is editable text, so it is recorded per entry rather than
   * looked up — a rename must not rewrite what the older entries say.
   */
  findingRef: z.string().nullable().optional(),
  /** On a `CREATED` from a duplicate: the version the findings came from. */
  copiedFromTemplateId: z.string().optional(),
  /** On a `CREATED` from the split action: the row it was split out of. */
  splitFromFindingId: z.string().optional(),
  /** On an activation: the versions it stood down by taking effect. */
  deactivatedTemplateIds: z.array(z.string()).optional(),
});

export type SocialScoreTemplateHistoryMetadata = z.infer<
  typeof SocialScoreTemplateHistoryMetadataSchema
>;

/** `metadata` of a `SOCIAL_SCORE_RULE_SET` entry. */
export const SocialScoreRuleSetHistoryMetadataSchema = z.object({
  entityType: z.literal(ENTITY_HISTORY_ENTITY_TYPES.SOCIAL_SCORE_RULE_SET),
  /** The revision's name as it stood. */
  ruleSetName: z.string().optional(),
  /** The revision this one was edited from. */
  basedOnRuleSetId: z.string().optional(),
  /**
   * The revisions this one retired by taking effect. Recorded here rather
   * than on theirs: a retired rule set's own timeline would say it stopped
   * being active without saying what replaced it.
   */
  retiredRuleSetIds: z.array(z.string()).optional(),
});

export type SocialScoreRuleSetHistoryMetadata = z.infer<
  typeof SocialScoreRuleSetHistoryMetadataSchema
>;

/** `metadata` of a `SOCIAL_SCORE_RUN` entry. */
export const SocialScoreRunHistoryMetadataSchema = z.object({
  entityType: z.literal(ENTITY_HISTORY_ENTITY_TYPES.SOCIAL_SCORE_RUN),
  /** The audit scored, so a run still reads after the audit is archived. */
  auditId: z.string().optional(),
  /**
   * What the run was scored against, recorded per entry rather than looked
   * up: the whole point of a run is that the library and the key move on,
   * and an older entry must keep saying which ones it used.
   */
  templateId: z.string().optional(),
  templateVersion: z.number().optional(),
  ruleSetId: z.string().optional(),
  /** On a run-level entry: the total as it stood. */
  totalScore: z.number().nullable().optional(),
  /**
   * On a match-level entry (`entity_sub_type` set): which library finding
   * the audit finding was matched to, and the audit finding itself. Both
   * recorded, because a correction is a change of one to the other and the
   * entry has to say which.
   */
  auditFindingId: z.string().optional(),
  findingRef: z.string().nullable().optional(),
  /** Whether the match being recorded came from the model or a person. */
  source: z.enum(['ai', 'human']).optional(),
  /** On a correction: what the model had proposed, so the two are comparable. */
  previousFindingRef: z.string().nullable().optional(),
});

export type SocialScoreRunHistoryMetadata = z.infer<
  typeof SocialScoreRunHistoryMetadataSchema
>;

export const EntityHistoryMetadataSchema = z.discriminatedUnion('entityType', [
  CompanyProfileHistoryMetadataSchema,
  AiProcessingJobHistoryMetadataSchema,
  CmmOrderLinkHistoryMetadataSchema,
  UserAccessHistoryMetadataSchema,
  IamRoleHistoryMetadataSchema,
  ServiceAccountHistoryMetadataSchema,
  SocialScoreTemplateHistoryMetadataSchema,
  SocialScoreRuleSetHistoryMetadataSchema,
  OrderHubTracingLinkHistoryMetadataSchema,
  OrderHubOrderHistoryMetadataSchema,
  AutomationRuleQuotaHistoryMetadataSchema,
  SocialScoreRunHistoryMetadataSchema,
  CompanyDocumentHistoryMetadataSchema,
  DevelopmentTagFeatureHistoryMetadataSchema,
  AllowedOriginHistoryMetadataSchema,
  TranslationHistoryMetadataSchema,
  UserProfileHistoryMetadataSchema,
  UserPreferenceHistoryMetadataSchema,
  QuestionnaireHistoryMetadataSchema,
  QuestionnaireRequestHistoryMetadataSchema,
  CollaborationHistoryMetadataSchema,
  QuestionnaireAnswerHistoryMetadataSchema,
  QuestionnaireCommentHistoryMetadataSchema,
  UserEmailSuppressionHistoryMetadataSchema,
  AssessmentBlueprintHistoryMetadataSchema,
  RuleTemplateHistoryMetadataSchema,
  RiskHistoryMetadataSchema,
  CertificationRuleHistoryMetadataSchema,
  CapaActionHistoryMetadataSchema,
  DocumentTypeHistoryMetadataSchema,
  AuditTypeHistoryMetadataSchema,
  AuditHistoryMetadataSchema,
  AuditFindingHistoryMetadataSchema,
  WebshopHistoryMetadataSchema,
  StyleHistoryMetadataSchema,
  PlatformFeatureHistoryMetadataSchema,
  CompanyFeatureHistoryMetadataSchema,
  TagIdentificationHistoryMetadataSchema,
  FacilityProcessHistoryMetadataSchema,
  AuditAccessRequestHistoryMetadataSchema,
  ProductDataPointHistoryMetadataSchema,
  ProductDataPointGroupHistoryMetadataSchema,
  CompanyMergeHistoryMetadataSchema,
  AmforiSupplierLinkHistoryMetadataSchema,
  AmforiSyncWarningHistoryMetadataSchema,
]);

export type EntityHistoryMetadata = z.infer<typeof EntityHistoryMetadataSchema>;
